import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Building2, MapPinned, Layers } from "lucide-react";
import { api } from "@/lib/api";
import { AffiliateBanner } from "@/components/AffiliateBanner";
import { SearchPanel } from "@/components/SearchPanel";
import { ListingCard } from "@/components/ListingCard";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/hooks/useSeo";
import { useHref, useT } from "@/i18n";
import { useCategoryLabel } from "@/i18n/categories";
import { categoryIcon } from "@/lib/categoryIcons";

const HERO = "https://images.pexels.com/photos/27118624/pexels-photo-27118624.jpeg";

export default function Home() {
  const navigate = useNavigate();
  const href = useHref();
  const t = useT();
  const catLabel = useCategoryLabel();
  const [locations, setLocations] = useState({ states: {}, categories: [] });
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({ listings: 0, states: 0, categories: 0 });
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/locations").then(({ data }) => setLocations(data));
    api.get("/settings/public").then(({ data }) => setSettings(data));
    api.get("/stats").then(({ data }) => setStats(data));
    api.get("/listings", { params: { limit: 12 } }).then(({ data }) => setFeatured(data.items));
  }, []);

  const runSearch = (params) => {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v));
    navigate(`${href("/directory")}?${query.toString()}`);
  };

  useSeo({
    title: `${t("home.eyebrow")} — DirektoriBiz.my`,
    description: t("home.subtitle"),
    canonical: `${window.location.origin}${window.location.pathname}`,
  });

  return (
    <div>
      <AffiliateBanner url={settings?.affiliate_url} testId="affiliate-banner-home" />

      <section className="relative grain overflow-hidden bg-slate-950">
        <img src={HERO} alt="Kuala Lumpur skyline" className="absolute inset-0 h-full w-full object-cover opacity-45" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-8 sm:py-24">
          <p className="rise text-xs uppercase tracking-[0.25em] text-sky-300">{t("home.eyebrow")}</p>
          <h1 className="rise mt-4 max-w-3xl font-display text-4xl font-extrabold leading-none tracking-tight text-white sm:text-5xl lg:text-6xl" style={{ animationDelay: "80ms" }}>
            {t("home.title")}
          </h1>
          <p className="rise mt-5 max-w-xl text-base leading-relaxed text-slate-200" style={{ animationDelay: "160ms" }}>
            {t("home.subtitle")}
          </p>

          <div className="rise mt-10" style={{ animationDelay: "240ms" }}>
            <SearchPanel locations={locations} onSearch={runSearch} dark />
          </div>

          <dl className="rise mt-10 grid max-w-2xl grid-cols-3 gap-4" style={{ animationDelay: "320ms" }}>
            {[
              { icon: Building2, label: t("home.stat.listings"), value: stats.listings, testId: "stat-listings" },
              { icon: MapPinned, label: t("home.stat.states"), value: stats.states, testId: "stat-states" },
              { icon: Layers, label: t("home.stat.categories"), value: stats.categories, testId: "stat-categories" },
            ].map((s) => (
              <div key={s.testId} data-testid={s.testId} className="rounded-lg border border-white/15 bg-white/10 p-4 backdrop-blur-md">
                <s.icon className="h-4 w-4 text-sky-300" />
                <dd className="mt-2 font-display text-2xl font-bold text-white">{s.value}</dd>
                <dt className="text-xs uppercase tracking-wider text-slate-300">{s.label}</dt>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("home.categories.eyebrow")}</p>
            <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.categories.title")}</h2>
          </div>
          <Button variant="ghost" data-testid="browse-all-button" onClick={() => navigate(href("/directory"))} className="text-primary hover:bg-accent">
            {t("home.viewAll")} <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {(locations.categories || []).map((c) => {
            const Icon = categoryIcon(c);
            return (
              <button
                key={c}
                data-testid={`home-category-${c}`}
                onClick={() => runSearch({ category: c })}
                className="group rounded-lg border border-border bg-card p-5 text-left transition-[transform,box-shadow,border-color] duration-200 hover:-translate-y-1 hover:border-primary hover:shadow-md"
              >
                <Icon className="h-5 w-5 text-primary transition-transform duration-200 group-hover:scale-110" />
                <span className="mt-3 block font-display text-base font-semibold tracking-tight text-slate-900">
                  {catLabel(c)}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-8 sm:px-8">
        <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{t("home.latest.eyebrow")}</p>
        <h2 className="mt-2 font-display text-2xl font-bold tracking-tight sm:text-3xl">{t("home.latest.title")}</h2>
        <div data-testid="home-featured-grid" className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {featured.map((l, i) => (
            <ListingCard key={l.id} listing={l} index={i} />
          ))}
        </div>

        <div className="mt-10 flex justify-center">
          <Button
            variant="outline"
            size="lg"
            data-testid="home-view-all-button"
            onClick={() => navigate(href("/directory"))}
            className="h-12 rounded-full border-slate-300 px-8 text-sm font-semibold transition-colors duration-200 hover:border-primary hover:text-primary"
          >
            {t("home.viewAllListings", { count: stats.listings })}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
